/* istanbul ignore file */
// Coverage ignore: this dialog is exercised only on real envs (web bundle
// running against a deployed `/config.json`); unit-testing it would require
// mocking Capacitor + the polling timer + a non-trivial React tree.
// Behavior is verified by manual smoke test on stg / partner / prod after
// each deploy that changes the bundle version.

import { Capacitor } from "@capacitor/core";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import { useTranslate } from "@tolgee/react";
import * as React from "react";
import { getDeployEnv } from "../../environment.js";

export const OutdatedVersionDialog = () => {
  const { t } = useTranslate("OutdatedDialog");
  const { outdatedVersionModalOpen, setOutdatedVersionModalOpen } =
    useOutdatedVersionCheck();

  return (
    <Dialog
      open={outdatedVersionModalOpen}
      onClose={() => setOutdatedVersionModalOpen(false)}
    >
      <DialogTitle>{t("We deployed a new application version")}</DialogTitle>
      <DialogContent>
        <DialogContentText>
          {t(
            "Please reload (CMD + R) your browser window if you experience any problems"
          )}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => setOutdatedVersionModalOpen(false)}
          variant="outlined"
        >
          {t("Not now")}
        </Button>
        <Button
          onClick={() => {
            setOutdatedVersionModalOpen(false);
            window.location.reload();
          }}
          color="primary"
          variant="contained"
          autoFocus
        >
          {t("Reload now")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

function useOutdatedVersionCheck() {
  const [outdatedVersionModalOpen, setOutdatedVersionModalOpen] =
    React.useState(false);

  React.useEffect(() => {
    if (getDeployEnv() === "local" || Capacitor.isNativePlatform()) return;

    let lastVersion = "";

    const handler = async () => {
      try {
        // MEIS-12358 Phase 8: poll /config.json `version` instead of the
        // separate /VERSION.json. Single runtime-config source; the deploy
        // stage writes both env-specific values and `version` together
        // with `Cache-Control: no-cache, no-store, must-revalidate`.
        // `cache: "no-store"` mirrors the bootstrap `loadRuntimeConfig()`
        // fetch in `runtimeConfig.ts` — without it an intermediary HTTP
        // cache could serve a stale `version` and the outdated-prompt
        // would never fire for some users after a deploy.
        const data = await (
          await fetch("/config.json", { cache: "no-store" })
        ).json();

        const latestVersion: string | undefined = data.version;
        if (!latestVersion) {
          // Old deploy stage hasn't written `version` yet — skip the
          // outdated check rather than false-positive on every poll.
          return;
        }

        if (!lastVersion) {
          lastVersion = latestVersion;
          return;
        }

        if (lastVersion && lastVersion !== latestVersion) {
          lastVersion = latestVersion;
          setOutdatedVersionModalOpen(true);
        }
      } catch (err) {
        console.info(err);
      }
    };

    window.addEventListener("focus", handler);

    return () => {
      window.removeEventListener("focus", handler);
    };
  }, []);

  return { outdatedVersionModalOpen, setOutdatedVersionModalOpen };
}
